import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { AUTH_EVENTS, type AuthRefreshedDetail } from '../api';
import { postLogout, postRefresh } from './authClient';
import { withRefreshLock } from './refreshLock';
import { getAccessToken, setAccessToken } from './tokenStore';
import type { AuthStatus, AuthUser } from './types';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** POST /auth/logout, clear in-memory token, flip to anonymous. */
  logout: () => Promise<void>;
  /** Force a /auth/refresh and update user + token. */
  refresh: () => Promise<void>;
  /** Apply a fresh AuthUser synchronously, without a /auth/refresh round-trip.
   *  Flips status to 'authenticated'. Use after an endpoint returns the updated
   *  user (e.g. verify-otp / register). */
  setUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Non-httpOnly session indicator the API sets alongside the httpOnly
// refreshToken (auth.controller.ts). Its only job is to tell the frontend
// "a refresh might succeed — try it" without exposing the token to JS.
const SESSION_INDICATOR_COOKIE = 'la_session';

function hasSessionIndicator(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split('; ').some((c) => c.startsWith(`${SESSION_INDICATOR_COOKIE}=`));
}

function clearSessionIndicator(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${SESSION_INDICATOR_COOKIE}=; Path=/; Max-Age=0`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() =>
    hasSessionIndicator() ? 'loading' : 'anonymous',
  );
  const [user, setUser] = useState<AuthUser | null>(null);

  // Guard so the boot-time refresh fires exactly once even under StrictMode.
  const bootedRef = useRef(false);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    if (!hasSessionIndicator()) {
      setStatus('anonymous');
      return;
    }

    void withRefreshLock(async () => postRefresh())
      .then((res) => {
        setAccessToken(res.data.tokens.accessToken);
        setUser(res.data.user);
        setStatus('authenticated');
      })
      .catch(() => {
        setAccessToken(null);
        clearSessionIndicator();
        setUser(null);
        setStatus('anonymous');
      });
  }, []);

  // React to refresh events triggered by apiFetch's 401 interceptor.
  useEffect(() => {
    function onRefreshed(e: Event): void {
      const detail = (e as CustomEvent<AuthRefreshedDetail>).detail;
      if (!detail) return;
      setUser(detail.user);
      setStatus('authenticated');
    }
    function onCleared(): void {
      setUser(null);
      setStatus('anonymous');
      clearSessionIndicator();
    }
    window.addEventListener(AUTH_EVENTS.refreshed, onRefreshed);
    window.addEventListener(AUTH_EVENTS.cleared, onCleared);
    return () => {
      window.removeEventListener(AUTH_EVENTS.refreshed, onRefreshed);
      window.removeEventListener(AUTH_EVENTS.cleared, onCleared);
    };
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const token = getAccessToken();
    if (token) {
      await postLogout(token);
    }
    setAccessToken(null);
    clearSessionIndicator();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await withRefreshLock(async () => postRefresh());
      setAccessToken(res.data.tokens.accessToken);
      setUser(res.data.user);
      setStatus('authenticated');
    } catch {
      setAccessToken(null);
      clearSessionIndicator();
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const applyUser = useCallback((nextUser: AuthUser): void => {
    setUser(nextUser);
    setStatus('authenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAuthenticated: status === 'authenticated',
      logout,
      refresh,
      setUser: applyUser,
    }),
    [status, user, logout, refresh, applyUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}
